<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { computed, onMounted, ref, useTemplateRef, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import ShareButton from '@/components/utilities/ShareButton.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import { useBuildingFacadesApi } from '@/composables/useBuildingFacadesApi';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useUnitsQuoteApi } from '@/composables/useUnitsQuoteApi';
import FileArrowDownRegular from '@/components/svg/FileArrowDownRegular.vue';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import UnitSummaryActions from './UnitSummaryActions.vue';
import UnitSummaryDetails from './UnitSummaryDetails.vue';
import UnitSummaryUnitAndBuildingPlan from './UnitSummaryUnitAndBuildingPlan.vue';
import type { Region } from '@/typings/regions/Region';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  building: Building,
  unit: Unit;
  region: Region;
  quoteInfo: NewQuoteInfo;
}>();

// Détecter la taille de l'écran pour mobile
const isMobile = ref(false);
const isTablet = ref(false);

const checkTablet = () => {
  isTablet.value = window.innerWidth <= 1024;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const { getUnitQuoteById } = useUnitsQuoteApi();
const { getBuildingFacades } = useBuildingFacadesApi();
const newQuoteInfo = ref<NewQuoteInfo>();
const buildingFacades = ref<BuildingFacade[]>([]);
const isLoading = ref(true);
const selectedConfigurationId = ref<string | undefined>(undefined);
const selectedStyleId = ref<number | undefined>(undefined);
const wizardStep = ref(5);
const unitName = computed(() => {
  const address = locale.value.toLowerCase() === 'fr-ca' ? props.unit.fullAddress : props.unit.fullAddressEn;
  return `${address}`;
});

const { getBuildingWizardUrlFromUnit } = useRentalLocalizedUrls();

onMounted(async () => {
  // Initialiser la détection mobile et tablette
  checkMobile();
  checkTablet();
  window.addEventListener('resize', checkMobile);
  window.addEventListener('resize', checkTablet);
  
  try {
    const optionRequest = {
      locale: locale.value.substring(0, 2),
    };
    const [quoteData, facadesData] = await Promise.all([
      getUnitQuoteById(props.unit.id, optionRequest),
      getBuildingFacades(props.building.id)
    ]);
    newQuoteInfo.value = quoteData;
    buildingFacades.value = facadesData;
  } catch (error) {
    console.error('Error fetching quote data:', error);
  } finally {
    isLoading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  window.removeEventListener('resize', checkTablet);
});

const dynamicPdfUrl = computed(() => {
  if (!newQuoteInfo.value?.unit?.id || selectedConfigurationId.value == null) {
    return null;
  }
  
  const baseUrl = '/umbraco/api/units/unit-pdf';
  const unitId = newQuoteInfo.value.unit.id;
  const configurationId = selectedConfigurationId.value;
  const currentLocale = locale.value || 'fr';
  
  return `${baseUrl}/${unitId}?selectedConfiguration=${configurationId}&locale=${currentLocale}`;
});

function onBackToUnitSelection() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('returnUrl')) {
    const returnUrl = params.get('returnUrl');
    if (returnUrl) {
      window.location.href = returnUrl;
    }
  } else {
    window.location.href = getBuildingWizardUrlFromUnit(props.unit);
  }  
}
</script>

<template>
  <!--Location - Étape 5 - Détails de l'unité-->
  <div class="unit-summary">
    <div class="wrapper wrapper_large">
      <LoadingSpinner v-if="isLoading" />

      <template v-else-if="newQuoteInfo">
        <!-- Étapes du wizard -->
        <UnitSelectionWizardSteps v-model="wizardStep" />
        <div class="main-content">
          <div class="unitSummary">
            <div class="unitSummary-header">
              <!--En-tête avec bouton de retour-->
              <HeaderWithBackButton :title="unitName"
                                    :hide-title="true"
                                    @back-click="onBackToUnitSelection" />
              <div class="header-actions">
                <div v-if="isTablet" class="pdfDownload">
                  <a v-if="dynamicPdfUrl"
                     :href="dynamicPdfUrl"
                     target="_blank">
                    <FileArrowDownRegular />
                  </a>
                </div>
                <ShareButton :unit-name="unitName" />
              </div>
            </div>
            <div v-if="isMobile" class="unitNameMobile">
              <h1>{{ unitName }}</h1>
            </div>

            <div class="unitSummary-container">
              <div class="unitSummary-plansContainer">
                <UnitSummaryUnitAndBuildingPlan :unit="newQuoteInfo.unit"
                                                :building-plan-image-url="building.buildingImageUrl"
                                                :building-facades="buildingFacades"
                                                :selected-configuration-id="selectedConfigurationId" />
              </div>
              <div class="unitSummary-detailsContainer">
                <UnitSummaryDetails :unit="newQuoteInfo.unit"
                                    :building="props.building"
                                    :unit-styles="newQuoteInfo.unitStyles"
                                    :options="[]"
                                    :quote-info="newQuoteInfo"
                                    @update:selectedConfigurationId="selectedConfigurationId = $event"
                                    @update:selectedStyleId="selectedStyleId = $event" />
              </div>
              <div class="unitSummary-actionsContainer">
                <UnitSummaryActions :quote-info="newQuoteInfo"
                                    :unit="newQuoteInfo.unit"
                                    :selected-configuration-id="selectedConfigurationId"
                                    :selected-style-id="selectedStyleId" />
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="unitSummary-error">
        {{ t('unitSummary.errorLoadingData') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/unitSummary.scss';
.unit-summary{
  .main-content{
    border: 0;
  }
}
</style>
